<template>
  <div class="m-tk">
    <!-- ==================== 数据未就绪 ==================== -->
    <div v-if="meta && meta.ready === false" class="card not-ready">
      <div class="nr-t">数据未就绪</div>
      <p>{{ meta.reason }}</p>
      <p>
        本页是<b>只读页面</b>：指标由本地脚本预计算落表（<code>trend_king_signal</code>）后同步到服务器，
        线上不做实时计算 —— 本页需要的<b>后复权长历史</b>在线上不存在，且 MA200 至少要 200 个交易日的历史。
      </p>
    </div>

    <template v-else>
      <!-- ==================== ① 信号日 + 小入口（提醒 / 详情 / 刷新） ==================== -->
      <div class="toolbar">
        <button type="button" class="dt" :disabled="loading" @click="showDate = true">
          {{ date || '—' }}<span class="cv">▾</span>
        </button>
        <span class="pool">全市场 {{ nf(universe) }} 只可算</span>
        <span class="sp" />
        <button type="button" class="tip-ico" aria-label="研究结论提醒" @click="showTip = true">⚠</button>
        <button type="button" class="doc-ico" @click="showNotice = true">详情</button>
        <button type="button" class="rf" :disabled="loading" @click="loadAll">
          <van-icon name="replay" />刷新
        </button>
      </div>

      <!-- 研究结论提醒（短弹层） -->
      <van-popup v-model="showTip" position="bottom" round :style="{ maxHeight: '70%' }">
        <div class="tip-pop">
          <div class="tip-pop-h">
            <span>研究结论</span>
            <van-icon name="cross" @click="showTip = false" />
          </div>
          <div class="tip-pop-b">
            <p>
              <b>查找 + 人工复核</b>工具，不是买入推荐 —— 本套条件全市场回测为<b>负超额</b>。
            </p>
            <p>
              本页只回答「今天有哪些股票同时满足这套右侧趋势条件」。
              用法是先缩到几十只，再人工逐只复核。
            </p>
            <button type="button" class="tip-more" @click="openNoticeFromTip">查看完整口径与结论 ›</button>
          </div>
        </div>
      </van-popup>
      <div v-if="lagDays" class="lagbar">
        ⚠️ 指标源滞后 {{ lagDays }} 个交易日（展示价已到 {{ barLatest }}）
      </div>

      <!-- ==================== ③ 数据概览（默认折叠，与年线偏离一致） ==================== -->
      <div class="ov">
        <button type="button" class="ov-head" @click="showOverview = !showOverview">
          <span>数据概览</span>
          <van-icon :name="showOverview ? 'arrow-up' : 'arrow-down'" />
        </button>
        <div v-show="showOverview" class="ov-body">
          <div class="grid2">
            <div class="stat">
              <div class="lb">当期通过信号</div>
              <div class="vl">{{ counts.current }}<small>只</small></div>
              <div class="sb">占可算样本 <b>{{ shareOf(counts.current) }}</b></div>
            </div>
            <div class="stat">
              <div class="lb">最严一环</div>
              <div class="vl sm">{{ narrowest.label }}</div>
              <div class="sb">{{ nf(narrowest.from) }} → {{ nf(narrowest.to) }}，砍掉 <b class="hl-bad">{{ narrowest.killPct }}</b></div>
            </div>
            <div class="stat">
              <div class="lb">市场环境（同日）</div>
              <div class="vl sm">
                <span class="up">{{ market.bull }}</span>
                <span class="muted">多头 /</span>
                <span class="down">{{ market.bear }}</span>
                <span class="muted">空头</span>
              </div>
              <div class="sb">空头是多头的 <b>{{ market.bearRatio === null || market.bearRatio === undefined ? '—' : market.bearRatio.toFixed(2) }}</b> 倍</div>
            </div>
            <div class="stat alert">
              <div class="lb">⚠️ 回测结论</div>
              <div class="vl">逐笔超额全为负</div>
              <div class="sb">{{ bt.configCount }} 个配置 · 达标概率最高仅 <b>{{ pct0(bt.mbbMax) }}</b>（要求 ≥ {{ pct0(bt.mbbRule) }}）</div>
            </div>
          </div>

          <div class="sec-h in-ov">
            <h3>筛选漏斗</h3>
            <span class="hint">{{ nf(universe) }} 只可算样本 → 逐级收窄</span>
          </div>
          <div class="funnel">
            <div v-for="(s, i) in funnel" :key="s.key" class="frow">
              <span class="fn" :class="{ 'fn-0': i === 0 }">{{ s.label }}</span>
              <span class="ftrack"><i class="ffill" :class="funnelLv(i)" :style="{ width: funnelW(s) }" /></span>
              <span class="fval">
                {{ nf(s.n) }}
                <em v-if="s.drop !== null" :class="s.drop < 0.3 ? 'mild' : ''">↓{{ pct0(s.drop) }}</em>
              </span>
            </div>
            <div v-if="counts.wide !== null" class="frow wide">
              <span class="fn">去 ④（更宽）</span>
              <span class="ftrack"><i class="ffill" :style="{ width: funnelW({ n: counts.wide }) }" /></span>
              <span class="fval">{{ counts.wide }}</span>
            </div>
            <p class="fnote">
              条长按 <b>√ 比例</b>（末级只占 {{ shareOf(counts.current) }}，线性比例下看不见）。
              「去 ④」= 回测里<b>唯一让超额改善</b>的一档。
            </p>
          </div>
        </div>
      </div>

      <!-- ==================== ④ 结果列表 ==================== -->
      <div class="sec-h">
        <h3>符合条件的股票</h3>
        <span class="hint">{{ rows.length }} 只 · 按 ADX 降序</span>
        <span class="sp" />
        <button type="button" class="act" @click="showFilters = true">筛选 ⚙</button>
      </div>

      <div v-if="error" class="errbar">{{ error }}</div>
      <van-loading v-else-if="loading && !rows.length" class="pl" vertical>正在读取趋势为王数据…</van-loading>
      <van-empty v-else-if="!loading && !rows.length" :description="emptyText" />

      <template v-else>
        <div class="card">
          <div
            v-for="r in pagedRows"
            :key="r.code"
            class="stock"
            :class="{ open: isOpen(r.code) }"
          >
            <button type="button" class="s-hd" @click="toggleExpand(r.code)">
              <span class="s-code">{{ r.code }}</span>
              <span class="s-name">{{ r.name || '—' }}</span>
              <span class="s-tag">{{ r.industry || '未分类' }} · {{ r.board }}</span>
              <span class="s-pct" :class="pctCls(r.pctChg)">
                {{ signed(r.pctChg) }}%<span class="s-px">{{ num(r.priceU, 2) }}</span>
              </span>
            </button>

            <div class="s-grid">
              <div class="c"><div class="k">ADX</div><div class="v">{{ num(r.adx, 1) }}</div></div>
              <div class="c"><div class="k">乖离20</div><div class="v">{{ pct1(r.dev20) }}</div></div>
              <div class="c"><div class="k">RSI</div><div class="v">{{ num(r.rsi, 1) }}</div></div>
              <div class="c"><div class="k">量比</div><div class="v">{{ num(r.volRatio, 2) }}</div></div>
            </div>

            <div class="s-ft">
              <span class="dots">
                <i class="dot" :class="filters.c1 ? 'on' : 'off'">①</i>
                <i class="dot" :class="filters.c1p ? 'on' : 'off'">①′</i>
                <i class="dot" :class="filters.c2 ? 'on' : 'off'">②</i>
                <i class="dot" :class="filters.c3 ? 'on' : 'off'">③</i>
                <i class="dot" :class="filters.c4 ? 'on' : 'off'">④</i>
                <i class="dot" :class="filters.exSt ? 'on' : 'off'">⑤</i>
              </span>
              <span class="note">多头排列 {{ r.alignDays }} 日</span>
              <button type="button" class="cv" :aria-expanded="isOpen(r.code) ? 'true' : 'false'" @click="toggleExpand(r.code)">
                {{ isOpen(r.code) ? '收起' : '明细' }} ▾
              </button>
            </div>

            <div v-show="isOpen(r.code)" class="s-more">
              <div class="cond">
                <span class="chip">① 均线多头</span>
                <span class="chip">①′ MA20 上行</span>
                <span class="chip">② ADX {{ num(r.adx, 1) }}</span>
                <span class="chip">③ RSI {{ num(r.rsi, 1) }}</span>
                <span class="chip" :class="r.c4up ? '' : 'warn'">
                  ④ 量比 {{ num(r.volRatio, 2) }}<template v-if="r.c4up">（收阳）</template>
                </span>
                <span class="chip">⑤ 非 ST</span>
              </div>

              <div class="kv">
                <div class="i"><div class="k">PDI / NDI</div><div class="v">{{ num(r.pdi, 1) }} / {{ num(r.ndi, 1) }}</div></div>
                <div class="i"><div class="k">乖离 50</div><div class="v">{{ pct1(r.dev50) }}</div></div>
                <div class="i"><div class="k">乖离 200</div><div class="v">{{ pct1(r.dev200) }}</div></div>
                <div class="i"><div class="k">多头持续</div><div class="v">{{ r.alignDays }} 日</div></div>
                <div class="i"><div class="k">流通市值</div><div class="v">{{ mcap(r.mcap) }}</div></div>
                <div class="i"><div class="k">板块</div><div class="v">{{ r.board }}</div></div>
              </div>

              <div class="devwrap">
                <div class="t">
                  MA20 乖离 {{ pct1(r.dev20) }}
                  · 处于当期 {{ rows.length }} 只的 <b>{{ devRank(r.dev20) === null ? '—' : devRank(r.dev20) + ' 分位' }}</b>
                  <template v-if="dev20Stats">
                    （分布：中位 {{ pct1(dev20Stats.median) }}，P75 {{ pct1(dev20Stats.p75) }}，最高 {{ pct1(dev20Stats.max) }}）
                  </template>
                </div>
                <div class="devbar"><i :style="{ left: (devRank(r.dev20) || 0) + '%' }" /></div>
              </div>

              <div class="drisk">
                ⚠️ 该股当日 <b>{{ signed(r.pctChg) }}%</b><template v-if="r.pctChg !== null && r.pctChg < 0">，属于本期「放量下跌」样本</template>。
                MA20 乖离 <b>{{ pct1(r.dev20) }}</b>、MA200 乖离 <b>{{ pct1(r.dev200) }}</b> —— {{ devBandText(r.dev20) }}
              </div>

              <div class="s-links">
                <a :href="stockstarCodeLink(r.code)" target="_blank" rel="noopener noreferrer">证券之星</a>
                <a :href="eastmoneyCodeLink(r)" target="_blank" rel="noopener noreferrer">东方财富</a>
              </div>
            </div>
          </div>
        </div>

        <div v-if="pageCount > 1" class="pager">
          <van-pagination
            v-model="page"
            :total-items="rows.length"
            :items-per-page="pageSize"
            force-ellipses
          />
        </div>
        <p class="fnote listnote">
          共 {{ rows.length }} 只 · 第 {{ page }} / {{ pageCount }} 页 · 展示价 = 不复权 VWAP（{{ priceCaliberShort }}）
        </p>
      </template>

      <!-- ==================== ⑥ 放量下跌诊断（列表下方常驻） ==================== -->
      <div v-if="volDiag" class="risk">
        <div class="hd">⚠ 放量下跌诊断 <span class="muted">（本页最该看的一栏 · ④ 口径，与上方开关无关）</span></div>
        <div class="bar">
          <div class="a" :style="{ width: upShare }">收涨 {{ volDiag.n - volDiag.downCount }} 只</div>
          <div class="b" :style="{ width: downShare }">下跌 {{ volDiag.downCount }}</div>
        </div>
        <div class="lg">
          <span><i class="lg-a" />收涨 {{ volDiag.n - volDiag.downCount }} 只</span>
          <span><i class="lg-b" />下跌 {{ volDiag.downCount }} 只（{{ pct1(volDiag.downPct) }}）</span>
          <span><i class="lg-c" />跌 ≥5% 共 {{ volDiag.deepDownCount }} 只</span>
        </div>
        <div v-if="volDiag.worst && volDiag.worst.length" class="worst">
          <div class="wt">跌幅最大 {{ volDiag.worst.length }} 只</div>
          <div v-for="w in volDiag.worst" :key="w.code" class="w">
            <span class="nm">{{ w.name || '—' }}</span>
            <span class="cd">{{ w.code }}</span>
            <span class="p down">{{ signed(w.pctChg) }}%</span>
          </div>
        </div>
        <p class="fnote">
          ④「量比 &gt; {{ thresholds.volRatio || 1.2 }}」抓到的<b>不是「放量上涨」</b>。
          {{ pct1(volDiag.downPct) }} 是放量下跌 —— 这就是为什么去掉 ④ 的那一档反而更好。
        </p>
      </div>
    </template>

    <!-- ==================== 日期选择 ==================== -->
    <van-popup v-model="showDate" position="bottom" round :style="{ maxHeight: '70%' }">
      <div class="sheet">
        <div class="handle" />
        <div class="sh">选择信号日 <button type="button" class="x" @click="showDate = false">✕</button></div>
        <div class="dlist">
          <button
            v-for="d in dates"
            :key="d"
            type="button"
            class="ditem"
            :class="{ on: d === date }"
            @click="pickDate(d)"
          >
            <span>{{ d }}</span>
            <span v-if="d === latestDate" class="dlatest">最新</span>
          </button>
        </div>
      </div>
    </van-popup>

    <!-- ==================== 筛选抽屉 ==================== -->
    <van-popup v-model="showFilters" position="bottom" round :style="{ maxHeight: '88%' }">
      <div class="sheet">
        <div class="handle" />
        <div class="sh">筛选条件 <button type="button" class="x" @click="showFilters = false">✕</button></div>

        <div class="seg">
          <button
            type="button"
            class="o"
            :class="{ on: presetIdx === 0 }"
            @click="applyPreset(true)"
          >
            <b>严格 · {{ counts.strict }} 只</b>含 ④ 量比
          </button>
          <button
            type="button"
            class="o"
            :class="{ on: presetIdx === 1 }"
            @click="applyPreset(false)"
          >
            <b>宽松 · {{ counts.wide }} 只</b>去 ④（回测更优）
          </button>
        </div>

        <div class="flist">
          <div v-for="f in filterDefs" :key="f.key" class="fitem">
            <div class="tx">
              <div class="n">{{ f.label }}</div>
              <div class="d" v-html="filterDesc(f)" />
            </div>
            <button
              type="button"
              class="sw"
              :class="{ on: filters[f.key], dis: f.dependsOn && !filters[f.dependsOn] }"
              :disabled="!!(f.dependsOn && !filters[f.dependsOn])"
              :aria-label="f.label"
              :aria-pressed="filters[f.key] ? 'true' : 'false'"
              @click="onToggle(f.key)"
            />
          </div>
        </div>

        <div class="btns">
          <button type="button" class="b g" @click="resetFilters">重置</button>
          <button type="button" class="b p" @click="showFilters = false">查看 {{ rows.length }} 只</button>
        </div>
      </div>
    </van-popup>

    <!-- ==================== 口径说明（全屏） ==================== -->
    <van-popup v-model="showNotice" position="bottom" :style="{ height: '100%' }">
      <div class="notice">
        <div class="nb-head">
          <button type="button" class="nback" @click="showNotice = false">‹ 返回</button>
          <span class="nt">页面说明与口径</span>
        </div>
        <div class="nb">
          <h4>⚠️ 这是一张「查找表」，不是荐股表</h4>
          <p>
            本页只回答一个问题：<b>今天有哪些股票同时满足这套右侧趋势条件</b>。
            它<b>不做买入推荐</b>，因为这套条件经全市场回测是<b>负超额</b>的。
          </p>

          <div class="quote">
            全市场 {{ nf(bt.universe && bt.universe.codes) }} 只 · {{ bt.universe && bt.universe.from }} ~ {{ bt.universe && bt.universe.to }} · {{ bt.configCount }} 个配置<br>
            <b>逐笔超额全为负</b>；组合层年化超额 {{ pct0(bt.portfolioAnnual && bt.portfolioAnnual[0]) }} ~ {{ pct0(bt.portfolioAnnual && bt.portfolioAnnual[1]) }}<br>
            达标概率最高 <b>{{ pct0(bt.mbbMax) }}</b>（项目验收线要求 ≥ {{ pct0(bt.mbbRule) }}）
          </div>

          <h4>四个条件的实际作用（消融结论）</h4>
          <ul>
            <li v-for="a in bt.ablation || []" :key="a.key">
              <b>{{ a.label }}</b> ——
              <template v-if="a.effect > 0">去掉它超额<b>改善</b> {{ a.effect.toFixed(4) }}pp（{{ a.key === 'c4' ? '最大拖累' : '拖累' }}）</template>
              <template v-else>去掉它超额<b>恶化</b> {{ Math.abs(a.effect).toFixed(4) }}pp ⇒ <b>唯一正贡献</b></template>
            </li>
            <li><b>④′ 放量须收阳</b> —— 已证伪：4 个持有期 × 3 个指标 = 12 组<b>全部更差</b></li>
          </ul>
          <p>⇒ 「条件越少越好」，但<b>整体仍然没有 alpha</b>。所以本页的用法是：<b>先用它把候选缩到几十只，再人工逐只复核</b>。</p>

          <h4>数据口径</h4>
          <ul>
            <li>指标用<b>后复权</b>价（<code>market_daily_hfq</code>）计算</li>
            <li>展示价用<b>不复权 VWAP</b>（<code>amount / volume</code>），与行情软件盘面一致，<b>不要拿它和均线相减</b></li>
            <li>可算样本 = 指标齐全（<code>MA200</code> 需该股 ≥ 200 个自身交易日）</li>
            <li>流通市值 = <code>amount / turnover</code>（唯一不受后复权污染的口径）</li>
            <li>信号日 <b>{{ date || '—' }}</b><template v-if="latestDate !== date">（最新 {{ latestDate }}）</template> · ST 判定为 point-in-time（覆盖率 {{ stShareText }}）</li>
            <li>本页<b>只读</b>：指标由本地脚本预计算落表后同步，线上不做实时计算</li>
          </ul>

          <h4>指标怎么看</h4>
          <ul>
            <li><b>ADX</b> ≥ {{ thresholds.adx || 25 }} = 有趋势（越大越强，但 &gt; 60 常见于急涨急跌）</li>
            <li><b>乖离20</b> = 收盘价对 MA20 的偏离，越大越「已经走远了」</li>
            <li><b>RSI</b> {{ thresholds.rsiLow || 50 }}~{{ thresholds.rsiHigh || 70 }} = 不弱也不超买</li>
            <li><b>量比</b> &gt; {{ thresholds.volRatio || 1.2 }} = 当日成交比近 {{ thresholds.volLookback || 20 }} 日均量放大</li>
          </ul>

          <p class="nb-foot">
            需要 <b>13 列宽表 / 导出 CSV</b> 请用电脑版访问同一地址；移动端刻意只保留人工复核真正要看的四个数字。
            阈值与回测脚本 <code>right-trend-sweep.js</code> 的 <code>TH</code> 常量一一对应。
          </p>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { request } from '../utils/http.js'
import { stockstarCodeLink, eastmoneyCodeLink } from '../utils/stockLink.js'

const PAGE_SIZE = 10

/** 抽屉里每个开关的一句话说明（结构文案；保留率/砍掉比例由 funnel 实时算） */
const FILTER_DESC = {
  c1: 'MA20 &gt; MA50 &gt; MA200（后复权）',
  c1p: '近 {maSlopeLook} 日 MA20 斜率 &gt; 0',
  c2: 'ADX ≥ {adx} 且 +DI &gt; −DI',
  c3: '不超买也不弱 · <b class="ok">唯一正贡献</b>',
  c4: '回测显示<b class="bad">去掉它更好</b>',
  c4up: '依赖 ④，④ 关掉时无意义 · <b class="bad">已回测 · 12 组全部更差</b>',
  exSt: 'point-in-time 剔除 ST / *ST',
}

export default {
  name: 'MobileTrendKing',
  data() {
    return {
      loading: false,
      error: '',
      meta: null,
      data: null,
      dates: [],
      latestDate: '',
      date: '',
      filters: { c1: true, c1p: true, c2: true, c3: true, c4: true, c4up: false, exSt: true },
      expanded: [],
      page: 1,
      pageSize: PAGE_SIZE,
      showDate: false,
      showFilters: false,
      showNotice: false,
      showTip: false,
      showOverview: false, // 数据概览默认折叠
      requestSeq: 0,
    }
  },
  computed: {
    thresholds() {
      return (this.data && this.data.thresholds) || (this.meta && this.meta.thresholds) || {}
    },
    /**
     * 口径串里「展示价」那一段的括号内容（如 amount/volume）。
     * ⚠️ 不能直接 `slice(indexOf('展示价用'))` —— 那样会把自己的「展示价 = 不复权 VWAP（…）」
     *    再套一层，拼出「不复权 VWAP（展示价用不复权 VWAP（amount/volume））」这种重复文案。
     */
    priceCaliberShort() {
      const p = (this.data && this.data.priceCaliber) || (this.meta && this.meta.priceCaliber) || ''
      const i = p.indexOf('展示价用')
      const seg = i >= 0 ? p.slice(i) : p
      const m = seg.match(/（([^）]*)）/)
      return m ? m[1] : ''
    },
    bt() {
      return (this.meta && this.meta.backtest) || (this.data && this.data.backtest) || {}
    },
    filterDefs() {
      return (this.meta && this.meta.filters) || []
    },
    universe() {
      return (this.data && this.data.market && this.data.market.universe) || 0
    },
    market() {
      return (this.data && this.data.market) || {}
    },
    funnel() {
      return (this.data && this.data.funnel) || []
    },
    counts() {
      return (this.data && this.data.counts) || { strict: null, wide: null, current: 0 }
    },
    rows() {
      return (this.data && this.data.rows) || []
    },
    volDiag() {
      return this.data && this.data.volDiag
    },
    dev20Stats() {
      return this.data && this.data.dev20Stats
    },
    lagDays() {
      return (this.meta && this.meta.dataLag && this.meta.dataLag.lagDays) || 0
    },
    barLatest() {
      return (this.meta && this.meta.dataLag && this.meta.dataLag.barLatest) || ''
    },
    stShareText() {
      const c = this.meta && this.meta.stCoverage
      if (!c || !c.pool) return '—'
      return `${((c.covered / c.pool) * 100).toFixed(2)}%（${c.covered} / ${c.pool} 只）`
    },
    /** 两档预设：严格（全默认含 ④）/ 宽松（去 ④）。其余条件被改过时不指向任何一档 */
    presetIdx() {
      const rest = ['c1', 'c1p', 'c2', 'c3', 'exSt'].every((k) => this.filters[k])
      if (!rest || this.filters.c4up) return -1
      return this.filters.c4 ? 0 : 1
    },
    narrowest() {
      let best = null
      for (const s of this.funnel) {
        if (s.drop === null || s.drop === undefined) continue
        if (!best || s.drop > best.drop) best = s
      }
      const idx = best ? this.funnel.indexOf(best) : -1
      const from = idx > 0 ? this.funnel[idx - 1].n : 0
      return {
        label: best ? best.label : '—',
        from,
        to: best ? best.n : 0,
        killPct: best ? this.pct0(best.drop) : '—',
      }
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.rows.length / this.pageSize))
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize
      return this.rows.slice(start, start + this.pageSize)
    },
    emptyText() {
      return this.error ? this.error : '当前开关组合下没有符合条件的股票'
    },
    upShare() {
      const d = this.volDiag
      if (!d || !d.n) return '0%'
      return `${((d.n - d.downCount) / d.n) * 100}%`
    },
    downShare() {
      const d = this.volDiag
      if (!d || !d.n) return '0%'
      return `${(d.downCount / d.n) * 100}%`
    },
    /** key → 该级在漏斗里的前后数量（抽屉里算保留率用） */
    funnelByKey() {
      const out = {}
      this.funnel.forEach((s, i) => {
        if (i === 0) return
        out[s.key] = { n: s.n, prev: this.funnel[i - 1].n, drop: s.drop }
      })
      return out
    },
    /** 每只股票 dev20 在当期结果里的位置（划片用） */
    devSorted() {
      return this.rows.map((r) => r.dev20).filter((v) => Number.isFinite(v)).sort((a, b) => a - b)
    },
  },
  created() {
    this.loadAll()
  },
  methods: {
    stockstarCodeLink,
    eastmoneyCodeLink,

    openNoticeFromTip() {
      this.showTip = false
      this.showNotice = true
    },

    /* ---------------- 格式化 ---------------- */
    nf(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toLocaleString('en-US') : '—'
    },
    num(v, d = 2) {
      return v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(d)
    },
    pct0(v) {
      return v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : `${(Number(v) * 100).toFixed(0)}%`
    },
    pct1(v) {
      return v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : `${(Number(v) * 100).toFixed(1)}%`
    },
    signed(v) {
      if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—'
      const n = Number(v)
      return `${n >= 0 ? '+' : ''}${n.toFixed(2)}`
    },
    pctCls(v) {
      const n = Number(v)
      if (!Number.isFinite(n) || n === 0) return 'muted'
      // A 股惯例：涨红跌绿（与桌面端 TrendKing.vue 同口径）
      return n > 0 ? 'up' : 'down'
    },
    mcap(v) {
      if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—'
      const n = Number(v)
      return n >= 10000 ? `${(n / 10000).toFixed(2)} 万亿` : `${n.toFixed(0)} 亿`
    },
    devBandText(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '乖离数据缺失'
      if (n > 0.2) return '这不是「刚启动」，是「已经走了很远」'
      if (n > 0.1) return '已经离 20 日均线偏远了，追高风险偏高'
      return '距 20 日均线尚近'
    },
    shareOf(n) {
      if (!this.universe) return '—'
      return `${((Number(n) / this.universe) * 100).toFixed(2)}%`
    },
    /** √ 比例：末级只占 0.74%，线性比例下完全看不见 */
    funnelW(s) {
      const max = this.funnel.length ? this.funnel[0].n : 0
      if (!max) return '0%'
      return `${Math.max(1.5, Math.sqrt(Number(s.n) / max) * 100)}%`
    },
    funnelLv(i) {
      if (i === 0) return 'l0'
      return i === this.funnel.length - 1 ? 'l6' : ''
    },
    devRank(v) {
      const list = this.devSorted
      if (!list.length || !Number.isFinite(Number(v))) return null
      const below = list.filter((x) => x <= Number(v)).length
      return Math.round((below / list.length) * 100)
    },
    isOpen(code) {
      return this.expanded.indexOf(code) >= 0
    },
    toggleExpand(code) {
      const i = this.expanded.indexOf(code)
      if (i >= 0) this.expanded.splice(i, 1)
      else this.expanded.push(code)
    },
    filterDesc(f) {
      let s = FILTER_DESC[f.key] || ''
      const th = this.thresholds
      s = s
        .replace('{adx}', th.adx === undefined ? '25' : th.adx)
        .replace('{maSlopeLook}', th.maSlopeLook === undefined ? '5' : th.maSlopeLook)
      const hit = this.funnelByKey[f.key]
      if (hit && hit.drop !== null && hit.drop !== undefined) {
        const keep = (1 - hit.drop) * 100
        s += ` · 保留率 <em>${hit.drop < 0 ? '—' : `${keep.toFixed(1)}%`}</em>`
      }
      if (f.key === 'exSt' && this.market && this.market.stCount !== null && this.market.stCount !== undefined) {
        s += ` · 全市场 ST <b>${this.market.stCount} 只</b>（覆盖 ${this.stShareText}）`
      }
      return s
    },

    /* ---------------- 数据 ---------------- */
    async loadAll() {
      this.loading = true
      this.error = ''
      try {
        const meta = await request('/api/trend-king/meta')
        this.meta = meta || {}
        if (meta && meta.ready === false) return
        this.dates = (meta && meta.dates) || []
        this.latestDate = (meta && meta.latestDate) || ''
        if (!this.date || this.dates.indexOf(this.date) < 0) this.date = this.latestDate
        const defs = (meta && meta.filters) || []
        const next = { ...this.filters }
        for (const f of defs) next[f.key] = f.default
        this.filters = next
        await this.loadSignals()
      } catch (err) {
        this.error = err.message || '加载趋势为王数据失败'
      } finally {
        this.loading = false
      }
    },
    async loadSignals() {
      if (!this.date) return
      const seq = ++this.requestSeq
      this.loading = true
      this.error = ''
      try {
        const qs = new URLSearchParams({ date: this.date })
        for (const k of Object.keys(this.filters)) qs.set(k, this.filters[k] ? '1' : '0')
        const res = await request(`/api/trend-king/signals?${qs.toString()}`)
        if (seq !== this.requestSeq) return
        this.data = res
        this.page = 1
        this.expanded = []
      } catch (err) {
        if (seq !== this.requestSeq) return
        this.data = null
        this.error = err.message || '加载信号列表失败'
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
    pickDate(d) {
      this.showDate = false
      if (d === this.date) return
      this.date = d
      this.loadSignals()
    },
    onToggle(key) {
      const f = this.filterDefs.find((x) => x.key === key)
      if (f && f.dependsOn && !this.filters[f.dependsOn]) return
      const next = { ...this.filters, [key]: !this.filters[key] }
      // ④′ 依赖 ④：④ 关掉时 ④′ 无意义，强制归零（与服务端 parseFilters 同一语义）
      if (key === 'c4' && !next.c4) next.c4up = false
      this.filters = next
      this.loadSignals()
    },
    applyPreset(strict) {
      this.filters = { c1: true, c1p: true, c2: true, c3: true, c4: strict, c4up: false, exSt: true }
      this.loadSignals()
    },
    resetFilters() {
      const defs = this.filterDefs
      const next = {}
      for (const f of defs) next[f.key] = f.default
      this.filters = next
      this.loadSignals()
    },
  },
}
</script>

<style scoped>
.m-tk {
  padding-bottom: 16px;
}

/* ---------- 数据未就绪 ---------- */
.not-ready {
  margin: 12px;
  padding: 14px;
}
.nr-t {
  font-size: 15px;
  font-weight: 600;
  color: #e6a23c;
}
.not-ready p {
  margin: 8px 0 0;
  font-size: 12.5px;
  color: #646566;
  line-height: 1.7;
}
.not-ready code {
  background: #f2f3f5;
  padding: 0 3px;
  border-radius: 3px;
  font-size: 11.5px;
}

/* ---------- 信号日 + 小入口 ---------- */
.toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 12px 0;
  padding: 9px 10px 9px 12px;
  background: #fff;
  border-radius: 10px;
  font-size: 13px;
}
.toolbar .dt {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #323233;
}
.toolbar .dt .cv {
  color: #c0c4cc;
  font-size: 11px;
}
.toolbar .pool {
  color: #646566;
  font-size: 12px;
}
.toolbar .sp { margin-left: auto; }
.toolbar .tip-ico {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid #f5dab1;
  border-radius: 50%;
  background: #fdf6ec;
  color: #e6a23c;
  font-size: 14px;
  font-family: inherit;
  line-height: 28px;
  text-align: center;
}
.toolbar .doc-ico {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 9px;
  border: 1px solid #dcdfe6;
  border-radius: 15px;
  background: #fff;
  color: #606266;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.toolbar .rf {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 12.5px;
  color: #1989fa;
}
.toolbar .dt:disabled,
.toolbar .rf:disabled {
  opacity: 0.5;
}

/* ---------- 研究结论短弹层 ---------- */
.tip-pop { padding: 14px 16px 18px; }
.tip-pop-h {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 600;
  color: #323233;
  margin-bottom: 10px;
}
.tip-pop-b { font-size: 13px; line-height: 1.7; color: #646566; }
.tip-pop-b p { margin: 0 0 10px; }
.tip-pop-b b { color: #b8860b; }
.tip-more {
  display: block;
  width: 100%;
  margin-top: 4px;
  padding: 10px 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #1989fa;
  text-align: center;
}
.lagbar {
  margin: 8px 12px 0;
  padding: 7px 11px;
  background: #fdf6ec;
  border-radius: 8px;
  font-size: 11.5px;
  color: #b8860b;
}

/* ---------- 数据概览（默认折叠） ---------- */
.ov {
  margin: 9px 12px 0;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
}
.ov-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 11px 12px;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #323233;
  text-align: left;
}
.ov-body { padding: 0 10px 10px; }
.sec-h.in-ov {
  margin: 12px 0 4px;
}

/* ---------- 概览 2×2 ---------- */
.grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
  margin: 0;
}
.stat {
  background: #f7f8fa;
  border-radius: 10px;
  padding: 8px 10px;
}
.stat .lb {
  font-size: 11px;
  color: #909399;
}
.stat .vl {
  margin-top: 2px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.22;
  color: #323233;
}
.stat .vl small {
  margin-left: 2px;
  font-size: 10.5px;
  font-weight: 400;
  color: #909399;
}
.stat .vl.sm {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}
.stat .sb {
  margin-top: 2px;
  font-size: 10.5px;
  line-height: 1.45;
  color: #909399;
}
.stat .sb b { color: #606266; }
.stat.alert { background: #fef0f0; }
.stat.alert .vl {
  font-size: 14px;
  color: #f56c6c;
}
.hl-bad { color: #f56c6c; }
.up { color: #f56c6c; }
.down { color: #67c23a; }
.muted { color: #909399; }

/* ---------- 区块标题 ---------- */
.sec-h {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin: 14px 12px 6px;
}
.sec-h h3 {
  margin: 0;
  font-size: 14.5px;
  font-weight: 600;
  color: #323233;
}
.sec-h .hint {
  font-size: 11.5px;
  color: #909399;
}
.sec-h .sp { margin-left: auto; }
.sec-h .act {
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: #1989fa;
}

/* ---------- 漏斗 ---------- */
.card {
  margin: 0 12px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
}
.funnel {
  padding: 6px 10px 8px;
  background: #f7f8fa;
  border-radius: 10px;
}
.frow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
}
.frow .fn {
  flex: 0 0 94px;
  font-size: 11.5px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.frow .fn-0 { color: #909399; }
.frow .ftrack {
  flex: 1 1 auto;
  height: 13px;
  background: #f0f2f5;
  border-radius: 7px;
  overflow: hidden;
}
.frow .ffill {
  display: block;
  height: 100%;
  border-radius: 7px;
  background: #79bbff;
}
.frow .ffill.l0 { background: #c8c9cc; }
.frow .ffill.l6 { background: #1989fa; }
.frow .fval {
  flex: 0 0 auto;
  min-width: 62px;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: #323233;
}
.frow .fval em {
  margin-left: 5px;
  font-style: normal;
  font-size: 11px;
  font-weight: 400;
  color: #f56c6c;
}
.frow .fval em.mild { color: #c0c4cc; }
.frow.wide .fn { color: #1989fa; }
.frow.wide .ffill {
  background: repeating-linear-gradient(45deg, #b3d8ff, #b3d8ff 4px, #d9ecff 4px, #d9ecff 8px);
}
.frow.wide .fval { color: #1989fa; }
.fnote {
  margin: 9px 0 0;
  font-size: 11px;
  line-height: 1.65;
  color: #a8abb2;
}
.fnote b { color: #909399; }

/* ---------- 结果卡片 ---------- */
.pl { padding: 48px 0; }
.errbar {
  margin: 10px 12px;
  padding: 10px 12px;
  background: #fef0f0;
  border-radius: 10px;
  font-size: 12.5px;
  color: #f56c6c;
}
.stock {
  padding: 11px 12px;
  border-bottom: 0.5px solid #f2f3f5;
}
.stock:last-child { border-bottom: 0; }
.s-hd {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  text-align: left;
}
.s-code {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: #323233;
}
.s-name {
  font-size: 14px;
  font-weight: 600;
  color: #323233;
}
.s-tag {
  flex: 0 0 auto;
  padding: 1px 5px;
  font-size: 10.5px;
  color: #646566;
  background: #f4f4f5;
  border-radius: 4px;
  max-width: 108px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.s-pct {
  margin-left: auto;
  font-size: 15px;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}
.s-px {
  margin-left: 6px;
  font-size: 11.5px;
  font-weight: 400;
  color: #909399;
}
.s-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-top: 9px;
}
.s-grid .c {
  background: #f7f8fa;
  border-radius: 7px;
  padding: 6px 7px;
}
.s-grid .c .k {
  font-size: 10.5px;
  color: #909399;
}
.s-grid .c .v {
  margin-top: 1px;
  font-size: 13px;
  font-weight: 700;
  color: #323233;
}
.s-ft {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 9px;
}
.dots { display: flex; gap: 3px; }
.dot {
  width: 17px;
  height: 17px;
  border-radius: 50%;
  font-size: 10px;
  line-height: 15px;
  text-align: center;
  font-weight: 700;
  font-style: normal;
}
.dot.on {
  background: #e8f4ff;
  color: #1989fa;
  border: 1px solid #b8dcff;
}
.dot.off {
  background: #f4f4f5;
  color: #c0c4cc;
  border: 1px solid #ebeef5;
}
.s-ft .note {
  font-size: 11px;
  color: #909399;
}
.s-ft .cv {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 11.5px;
  color: #1989fa;
}
.stock.open .s-ft .cv { color: #909399; }

/* ---------- 展开明细 ---------- */
.s-more {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 0.5px dashed #e4e7ed;
}
.cond {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.chip {
  padding: 2px 7px;
  font-size: 11px;
  color: #529b2e;
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  border-radius: 5px;
}
.chip.warn {
  color: #b8860b;
  background: #fdf6ec;
  border-color: #f5dab1;
}
.kv {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px 6px;
  margin-top: 8px;
}
.kv .i .k {
  font-size: 10.5px;
  color: #909399;
}
.kv .i .v {
  margin-top: 1px;
  font-size: 13px;
  font-weight: 600;
  color: #323233;
}
.devwrap { margin-top: 8px; }
.devwrap .t {
  margin-bottom: 4px;
  font-size: 10.5px;
  color: #909399;
  line-height: 1.6;
}
.devwrap .t b { color: #303133; }
.devbar {
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(90deg, #e8f4ff, #f5dab1, #f56c6c);
  position: relative;
}
.devbar i {
  position: absolute;
  top: -3px;
  width: 2px;
  height: 12px;
  background: #323233;
  border-radius: 1px;
}
.drisk {
  margin-top: 8px;
  padding: 7px 10px;
  background: #fef0f0;
  border-radius: 8px;
  font-size: 11.5px;
  line-height: 1.6;
  color: #c0392b;
}
.s-links {
  display: flex;
  gap: 14px;
  margin-top: 8px;
}
.s-links a {
  font-size: 11.5px;
  color: #1989fa;
  text-decoration: none;
}

.pager { padding: 12px 8px 4px; }
.listnote { margin: 6px 12px 0; }

/* ---------- 放量下跌诊断 ---------- */
.risk {
  margin: 10px 12px;
  padding: 11px;
  background: #fff;
  border-radius: 12px;
}
.risk .hd {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #323233;
}
.risk .hd .muted {
  font-size: 11px;
  font-weight: 400;
}
.risk .bar {
  display: flex;
  height: 22px;
  margin-top: 8px;
  border-radius: 6px;
  overflow: hidden;
  font-size: 11px;
  line-height: 22px;
  color: #fff;
}
.risk .bar .a { background: #67c23a; text-align: center; }
.risk .bar .b { background: #f56c6c; text-align: center; }
.risk .lg {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 6px;
  font-size: 11.5px;
  color: #606266;
}
.risk .lg i {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  border-radius: 2px;
}
.lg-a { background: #67c23a; }
.lg-b { background: #f56c6c; }
.lg-c { background: #c0392b; }
.risk .worst {
  margin-top: 7px;
  padding-top: 7px;
  border-top: 0.5px solid #f2f3f5;
}
.risk .worst .wt {
  margin-bottom: 4px;
  font-size: 11.5px;
  color: #909399;
}
.risk .worst .w {
  display: flex;
  align-items: center;
  padding: 3px 0;
  font-size: 12.5px;
}
.risk .worst .w .nm { color: #323233; }
.risk .worst .w .cd {
  margin-left: 6px;
  font-size: 11px;
  color: #909399;
}
.risk .worst .w .p {
  margin-left: auto;
  font-weight: 700;
}

/* ---------- 弹层公共 ---------- */
.sheet {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  padding: 6px 16px 16px;
}
.sheet .handle {
  width: 36px;
  height: 4px;
  margin: 6px auto 10px;
  border-radius: 2px;
  background: #dcdee0;
  flex: 0 0 auto;
}
.sheet .sh {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #323233;
  flex: 0 0 auto;
}
.sheet .sh .x {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 18px;
  line-height: 1;
  color: #c8c9cc;
}
.sheet .seg {
  display: flex;
  gap: 3px;
  margin-top: 12px;
  padding: 3px;
  background: #f2f3f5;
  border-radius: 9px;
  flex: 0 0 auto;
}
.sheet .seg .o {
  flex: 1;
  padding: 7px 0;
  border: 0;
  border-radius: 7px;
  background: none;
  font-family: inherit;
  font-size: 12.5px;
  line-height: 1.35;
  color: #646566;
}
.sheet .seg .o b {
  display: block;
  font-size: 14px;
}
.sheet .seg .o.on {
  background: #fff;
  color: #1989fa;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.sheet .flist {
  margin-top: 12px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.sheet .fitem {
  display: flex;
  align-items: center;
  padding: 11px 0;
  border-bottom: 0.5px solid #f2f3f5;
}
.sheet .fitem:last-child { border-bottom: 0; }
.sheet .fitem .tx {
  flex: 1;
  min-width: 0;
}
.sheet .fitem .tx .n {
  font-size: 14px;
  color: #323233;
}
.sheet .fitem .tx .d {
  margin-top: 2px;
  font-size: 11px;
  line-height: 1.5;
  color: #909399;
}
.sheet .fitem .tx .d em {
  font-style: normal;
  color: #f56c6c;
}
.sheet .fitem .tx .d .ok { color: #529b2e; }
.sheet .fitem .tx .d .bad { color: #f56c6c; }
.sw {
  flex: 0 0 auto;
  width: 44px;
  height: 25px;
  margin-left: 10px;
  padding: 0;
  border: 0;
  border-radius: 13px;
  background: #dcdee0;
  position: relative;
  transition: background 0.18s;
}
.sw::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 21px;
  height: 21px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.18s;
}
.sw.on { background: #1989fa; }
.sw.on::after { transform: translateX(19px); }
.sw.dis { opacity: 0.45; }
.sheet .btns {
  display: flex;
  gap: 10px;
  margin-top: 14px;
  flex: 0 0 auto;
}
.sheet .btns .b {
  flex: 1;
  padding: 11px 0;
  border: 0;
  border-radius: 9px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
}
.sheet .btns .b.g {
  background: #f2f3f5;
  color: #646566;
}
.sheet .btns .b.p {
  background: #1989fa;
  color: #fff;
}

/* ---------- 日期选择 ---------- */
.dlist {
  margin-top: 8px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.ditem {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 2px;
  border: 0;
  border-bottom: 0.5px solid #f2f3f5;
  background: none;
  font-family: inherit;
  font-size: 14px;
  color: #323233;
  text-align: left;
}
.ditem.on {
  color: #1989fa;
  font-weight: 600;
}
.dlatest {
  margin-left: 8px;
  padding: 1px 6px;
  font-size: 10.5px;
  font-weight: 400;
  color: #1989fa;
  background: #e8f4ff;
  border-radius: 4px;
}

/* ---------- 口径说明（全屏） ---------- */
.notice {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}
.nb-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  height: 46px;
  padding: 0 12px;
  border-bottom: 0.5px solid #ebedf0;
  position: relative;
}
.nback {
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 14px;
  color: #1989fa;
}
.nb-head .nt {
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #323233;
  pointer-events: none;
}
.nb {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 16px 24px;
}
.nb h4 {
  margin: 16px 0 6px;
  font-size: 14px;
  color: #323233;
}
.nb p {
  margin: 0 0 8px;
  font-size: 12.5px;
  line-height: 1.75;
  color: #606266;
}
.nb ul {
  margin: 0 0 8px;
  padding-left: 18px;
  font-size: 12.5px;
  line-height: 1.8;
  color: #606266;
}
.nb code {
  background: #f2f3f5;
  border-radius: 3px;
  padding: 0 3px;
  font-size: 11.5px;
}
.nb .quote {
  padding: 9px 11px;
  background: #f7f8fa;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.7;
  color: #646566;
}
.nb .quote b { color: #f56c6c; }
.nb-foot {
  margin-top: 14px !important;
  font-size: 11.5px !important;
  color: #909399 !important;
}
</style>

<!-- 项目没有全局 box-sizing 重置：不补这一块，卡片会被 padding + border 撑宽导致横向溢出 -->
<style>
.m-tk,
.m-tk * {
  box-sizing: border-box;
}
</style>
